import { API } from '../config';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);


export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [akisToken, setAkisToken] = useState(null);

  const getAuthHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setUser(res.data);
    } catch (err) {
      console.error('Auth check failed:', err);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password, isAdmin = false) => {
    const endpoint = isAdmin ? `${API}/admin/login` : `${API}/auth/login`;
    const clientId = localStorage.getItem('client_instance_id') || (() => {
      const id = Math.random().toString(36).substr(2, 12);
      localStorage.setItem('client_instance_id', id);
      return id;
    })();
    const res = await axios.post(endpoint, { email, password, client_instance_id: clientId });
    const { token: newToken, user: userData, device_pending, app_lock_enabled } = res.data;
    if (device_pending) {
      throw { response: { data: { detail: 'Cihaz yönetici onayı bekliyor. Lütfen bekleyin.' } }, devicePending: true };
    }
    localStorage.setItem('token', newToken);
    setToken(newToken);
    // Merge app_lock_enabled into user data
    setUser({ ...userData, app_lock_enabled });
    return { ...userData, app_lock_enabled };
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: getAuthHeaders() });
    } catch (err) {
      console.error('Logout error:', err);
    }
    // Clear WhatsApp session data for this user
    try {
      const uid = user?.id || '';
      Object.keys(localStorage).forEach(k => { if (k.startsWith(`wa_${uid}`)) localStorage.removeItem(k); });
    } catch {}
    localStorage.removeItem('token');
    sessionStorage.removeItem('pin_verified');
    setToken(null);
    setUser(null);
    setAkisToken(null);
  };

  const panic = async () => {
    try {
      await axios.post(`${API}/auth/panic`, {}, { headers: getAuthHeaders() });
    } catch (err) {
      console.error('Panic error:', err);
    }
    localStorage.clear();
    sessionStorage.clear();
    setToken(null);
    setUser(null);
    setAkisToken(null);
  };

  const authenticateAkis = async (pin) => {
    const res = await axios.post(`${API}/admin/akis/auth`, { pin }, { headers: getAuthHeaders() });
    setAkisToken(res.data.akis_token);
    return res.data.akis_token;
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, akisToken,
      login, register, logout, panic, authenticateAkis,
      getAuthHeaders, setAkisToken,
      isAuthenticated: !!user,
      isAdmin: user?.role === 'admin'
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
